import { useState } from 'react';
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import { Button, Card, Input, Badge, Spinner } from '@realvisor/ui';
import {
  useProjectApiKeys,
  useCreateApiKey,
  useToggleApiKey,
  useDeleteApiKey,
  type ProjectApiKey,
} from '@/hooks/useApiKeys';

// ============================================
// JSON příklady pro každý typ formuláře
// ============================================

function getApiPayloadExample(formType?: string): { description: string; json: string } {
  const apiUrl = import.meta.env['VITE_API_URL'] || 'https://api-production-88cf.up.railway.app';
  const endpoint = `${apiUrl}/api/v1/public/api-leads/submit`;

  switch (formType) {
    case 'hypokalkulacka':
      return {
        description: 'Hypokalkulačka — lead s daty o hypotéce',
        json: JSON.stringify({
          firstName: 'Jan',
          lastName: 'Novák',
          email: 'jan@example.com',
          phone: '+420 123 456 789',
          message: 'Mám zájem o hypotéku na koupi bytu',
          data: {
            purpose: 'purchase',
            propertyPrice: 5000000,
            ownFunds: 1000000,
            householdIncome: '50-70',
            hasLiabilities: false,
            ageGroup: 'under36',
            loanAmount: 4000000,
            monthlyPayment: 21000,
            approvalStatus: 'green',
          },
        }, null, 2),
      };

    case 'prescoring':
      return {
        description: 'Prescoring — předběžné posouzení hypotéky',
        json: JSON.stringify({
          firstName: 'Jana',
          lastName: 'Nováková',
          email: 'jana@example.com',
          phone: '+420 987 654 321',
          data: {
            purpose: 'purchase',
            householdIncome: '80-120',
            monthlyLiabilities: 'none',
            ownFundsPercent: '10-20',
            score: 3,
            status: 'green',
          },
        }, null, 2),
      };

    case 'valuo':
      return {
        description: 'Valuo — ocenění nemovitosti',
        json: JSON.stringify({
          firstName: 'Petr',
          lastName: 'Svoboda',
          email: 'petr@example.com',
          phone: '+420 111 222 333',
          message: 'Chci ocenění bytu 3+1 v Praze',
          data: {
            propertyType: 'flat',
            address: 'Vinohradská 10, Praha 2',
            floorArea: 85,
            localType: '3+1',
            ownership: 'private',
            rating: 'good',
          },
        }, null, 2),
      };

    default:
      return {
        description: 'Kontaktní formulář — základní lead',
        json: JSON.stringify({
          firstName: 'Jan',
          lastName: 'Novák',
          email: 'jan@example.com',
          phone: '+420 123 456 789',
          message: 'Mám zájem o vaše služby',
          data: {
            source: 'web',
            campaign: 'letni-akce',
          },
        }, null, 2),
      };
  }
}

function getCurlExample(apiKey: string, formType?: string): string {
  const apiUrl = import.meta.env['VITE_API_URL'] || 'https://api-production-88cf.up.railway.app';
  const payload = getApiPayloadExample(formType);
  return `curl -X POST "${apiUrl}/api/v1/public/api-leads/submit" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '${payload.json}'`;
}

// ============================================
// COMPONENT
// ============================================

interface ApiAccessSectionProps {
  projectId: string;
  formType?: string; // 'contact' | 'valuo' | 'hypokalkulacka' | 'prescoring'
}

export default function ApiAccessSection({ projectId, formType }: ApiAccessSectionProps) {
  const { data: apiKeys, isLoading } = useProjectApiKeys(projectId);
  const createApiKey = useCreateApiKey();
  const toggleApiKey = useToggleApiKey();
  const deleteApiKey = useDeleteApiKey();

  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showFullKey, setShowFullKey] = useState(false);

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      toast.error('Zadejte název API klíče');
      return;
    }

    try {
      const result = await createApiKey.mutateAsync({
        projectId,
        name: newKeyName.trim(),
      });
      setNewlyCreatedKey(result.apiKey || null);
      setNewKeyName('');
      setShowCreate(false);
      toast.success('API klíč vytvořen');
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se vytvořit API klíč');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await toggleApiKey.mutateAsync(id);
      toast.success('Stav klíče změněn');
    } catch {
      toast.error('Nepodařilo se změnit stav klíče');
    }
  };

  const handleDelete = async (key: ProjectApiKey) => {
    if (!confirm(`Opravdu chcete smazat API klíč "${key.name}"? Tato akce je nevratná.`)) return;

    try {
      await deleteApiKey.mutateAsync(key.id);
      toast.success('API klíč smazán');
    } catch {
      toast.error('Nepodařilo se smazat API klíč');
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} zkopírován do schránky`);
  };

  const payloadExample = getApiPayloadExample(formType);

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <KeyIcon className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">API přístup</h2>
        </div>
        <Button
          size="sm"
          leftIcon={<PlusIcon className="h-4 w-4" />}
          onClick={() => setShowCreate(true)}
        >
          Nový klíč
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Posílejte leady přímo přes REST API bez embed widgetu. Každý klíč je vázaný na tento projekt.
      </p>

      {/* Nově vytvořený klíč — zobrazit jen jednou */}
      {newlyCreatedKey && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-green-800">Nový API klíč vytvořen</span>
            <Badge variant="warning">Zkopírujte si ho — nebude znovu zobrazen</Badge>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm bg-green-100 px-3 py-2 rounded font-mono break-all">
              {showFullKey ? newlyCreatedKey : `${newlyCreatedKey.slice(0, 12)}${'*'.repeat(32)}${newlyCreatedKey.slice(-4)}`}
            </code>
            <button
              onClick={() => setShowFullKey(!showFullKey)}
              className="p-2 text-green-600 hover:text-green-800"
              title={showFullKey ? 'Skrýt' : 'Zobrazit'}
            >
              {showFullKey ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
            <button
              onClick={() => copyToClipboard(newlyCreatedKey, 'API klíč')}
              className="p-2 text-green-600 hover:text-green-800"
              title="Kopírovat"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => { setNewlyCreatedKey(null); setShowFullKey(false); }}
            className="mt-2 text-xs text-green-700 hover:text-green-900 underline"
          >
            Zavřít (klíč si uložte)
          </button>
        </div>
      )}

      {/* Formulář pro vytvoření */}
      {showCreate && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Nový API klíč</h3>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-600 mb-1">Název (pro vaši identifikaci)</label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Např. Můj CRM, Zapier, Make.com..."
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <Button onClick={handleCreate} disabled={createApiKey.isPending}>
              {createApiKey.isPending ? 'Vytvářím...' : 'Vytvořit'}
            </Button>
            <Button variant="outline" onClick={() => { setShowCreate(false); setNewKeyName(''); }}>
              Zrušit
            </Button>
          </div>
        </div>
      )}

      {/* Seznam klíčů */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : apiKeys && apiKeys.length > 0 ? (
        <div className="space-y-2 mb-6">
          {apiKeys.map((key) => (
            <div
              key={key.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                key.isActive ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{key.name}</span>
                  <Badge variant={key.isActive ? 'success' : 'default'}>
                    {key.isActive ? 'Aktivní' : 'Neaktivní'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded">{key.apiKeyHint}</code>
                  {key.lastUsedAt && (
                    <span>
                      Poslední použití: {(() => {
                        try {
                          return format(new Date(key.lastUsedAt), 'd. M. yyyy HH:mm', { locale: cs });
                        } catch { return '-'; }
                      })()}
                    </span>
                  )}
                  <span>
                    Vytvořen: {(() => {
                      try {
                        return format(new Date(key.createdAt), 'd. M. yyyy', { locale: cs });
                      } catch { return '-'; }
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleToggle(key.id)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors text-xs"
                  title={key.isActive ? 'Deaktivovat' : 'Aktivovat'}
                >
                  {key.isActive ? 'Deaktivovat' : 'Aktivovat'}
                </button>
                <button
                  onClick={() => handleDelete(key)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Smazat"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 mb-6 bg-gray-50 rounded-lg">
          <KeyIcon className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">Zatím žádné API klíče</p>
          <p className="text-xs text-gray-400">Vytvořte klíč pro odesílání leadů přes API</p>
        </div>
      )}

      {/* API dokumentace */}
      <div className="border-t border-gray-200 pt-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Jak použít API</h3>

        {/* Endpoint */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Endpoint</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded font-mono">
              POST {import.meta.env['VITE_API_URL'] || 'https://api-production-88cf.up.railway.app'}/api/v1/public/api-leads/submit
            </code>
            <button
              onClick={() => copyToClipboard(
                `${import.meta.env['VITE_API_URL'] || 'https://api-production-88cf.up.railway.app'}/api/v1/public/api-leads/submit`,
                'Endpoint'
              )}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Headers */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Headers</label>
          <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
            <code className="text-sm text-green-400 whitespace-pre">{`Content-Type: application/json
X-API-Key: <VÁŠ_API_KLÍČ>`}</code>
          </div>
        </div>

        {/* JSON payload */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-gray-600">
              JSON Body — {payloadExample.description}
            </label>
            <button
              onClick={() => copyToClipboard(payloadExample.json, 'JSON')}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <ClipboardDocumentIcon className="h-3 w-3" />
              Kopírovat
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
            <code className="text-sm text-green-400 whitespace-pre">{payloadExample.json}</code>
          </div>
        </div>

        {/* cURL příklad */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-gray-600">cURL příklad</label>
            <button
              onClick={() => copyToClipboard(
                getCurlExample(newlyCreatedKey || '<VÁŠ_API_KLÍČ>', formType),
                'cURL'
              )}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <ClipboardDocumentIcon className="h-3 w-3" />
              Kopírovat
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
            <code className="text-sm text-green-400 whitespace-pre">
              {getCurlExample(newlyCreatedKey || '<VÁŠ_API_KLÍČ>', formType)}
            </code>
          </div>
        </div>

        {/* Response */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Response (200 OK)</label>
          <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
            <code className="text-sm text-green-400 whitespace-pre">{JSON.stringify({
              success: true,
              leadId: 'uuid-of-created-lead',
              contactId: 'uuid-of-contact',
            }, null, 2)}</code>
          </div>
        </div>

        {/* Poznámky */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Poznámky</h4>
          <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
            <li><strong>email</strong> nebo <strong>phone</strong> je povinný (alespoň jedno)</li>
            <li><strong>data</strong> objekt může obsahovat libovolná vlastní pole</li>
            <li>API klíč je vázaný na projekt — lead se automaticky vytvoří v tomto projektu</li>
            <li>Kontakt se automaticky najde (podle emailu/telefonu) nebo vytvoří nový</li>
            <li>Lead se zařadí do první fáze výchozí pipeline projektu</li>
          </ul>
        </div>
      </div>
    </Card>
  );
}
